import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { DollarSign, Heart, Laptop, Scale, Settings, LogOut, Plus, Users, Calendar, Bell, AlertCircle, LayoutDashboard, Cat, BookOpen, LifeBuoy, Plane, Share2, Folder, FileText, Calculator, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { DashboardSkeleton } from '../common/SkeletonLoader';
import { DigitalEstatePlanning } from '../DigitalEstatePlanning';
import { LegalEstatePlanning } from '../LegalEstatePlanning';
import { FinancialManagement } from '../FinancialManagement';
import { HealthManagement } from '../HealthManagement';

import { PetsManagement } from '../PetsManagement';
import { NDISManagement } from '../NDISManagement';
import { LegacyManagement } from '../LegacyManagement';
import { TravelManagement } from '../TravelManagement';
import { WidgetSidebar } from '../Sidebar/WidgetSidebar';
import { SidebarConfigModal, SidebarItem, getIconComponent } from '../SidebarConfig/SidebarConfigModal';
import { WellbeingManagement } from '../WellbeingManagement';
import SettingsDashboard from '../Settings/SettingsDashboard';
import { Breadcrumb } from '../common/Breadcrumb';
import TrustedAdvisorManagement from '../TrustedAdvisorManagement';
import SharedWithMe from '../SharingManagement/SharedWithMe';
import { ComingSoonPage } from './ComingSoonPage';
import { ScoreService } from '../../lib/scoreService';
import MyFilesManagement from '../MyFilesManagement';
import { BudgetingManagement } from '../BudgetingManagement';
import { EmergencyManagement } from '../EmergencyManagement';
import { safeLocalStorage } from '../../lib/utils';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
  className?: string;
}

const NavItem = React.memo<NavItemProps>(({ icon, label, active, onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors text-idecide-dark
      ${active ? 'bg-idecide-primary font-medium' : 'hover:bg-idecide-primary hover:bg-opacity-50'} ${className}`}
  >
    {icon}
    <span>{label}</span>
  </button>
));

NavItem.displayName = 'NavItem';

export default function Dashboard({ initialSection }: { initialSection?: string }) {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(initialSection || 'dashboard');
  const [showSidebarConfig, setShowSidebarConfig] = useState(false);
  // Default sidebar items
  const defaultSidebarItems: SidebarItem[] = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', iconName: 'dashboard', section: 'dashboard', visible: true },
    { id: 'digital', label: 'Digital', iconName: 'digital', section: 'digital', visible: true },
    { id: 'legal', label: 'Legal', iconName: 'legal', section: 'legal', visible: true },
    { id: 'financial', label: 'Financial', iconName: 'financial', section: 'financial', visible: true },
    { id: 'budgeting', label: 'Budgeting', iconName: 'budgeting', section: 'budgeting', visible: true },
    { id: 'health', label: 'Health', iconName: 'health', section: 'health', visible: true },

    { id: 'wellbeing', label: 'Wellbeing', iconName: 'wellbeing', section: 'wellbeing', visible: true },
    { id: 'pets', label: 'Pets', iconName: 'pets', section: 'pets', visible: true },
    { id: 'ndis', label: 'NDIS', iconName: 'ndis', section: 'ndis', visible: true },
    { id: 'legacy', label: 'Legacy', iconName: 'legacy', section: 'legacy', visible: true },
    { id: 'travel', label: 'Travel', iconName: 'travel', section: 'travel', visible: true },
    { id: 'files', label: 'My Files', iconName: 'files', section: 'files', visible: true },
    { id: 'emergency', label: 'Emergency', iconName: 'emergency', section: 'emergency', visible: true },
    { id: 'calendar', label: 'Calendar', iconName: 'calendar', section: 'calendar', visible: false }
  ], []);

  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>(defaultSidebarItems);
  const [localProfile, setLocalProfile] = useState(profile);
  const [useCustomWidgets, setUseCustomWidgets] = useState(true);
  const [lastTabRefresh, setLastTabRefresh] = useState<number>(Date.now());

  // Refresh dashboard data when the tab becomes visible again.
  // AuthContext already handles session/token recovery on visibilitychange,
  // so this handler only needs to refresh dashboard-level data *after* the
  // session is guaranteed valid (we add a small delay to let AuthContext go first).
  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastRefreshTime = 0;
    let lastHiddenAt = 0; // track when tab was hidden so we know the real absence duration
    const MIN_REFRESH_INTERVAL = 60_000; // 1-minute cooldown between full refreshes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }

      if (document.visibilityState !== 'visible' || !user) return;

      const now = Date.now();
      if (now - lastRefreshTime < MIN_REFRESH_INTERVAL) return;

      // How long was the tab hidden?
      const absenceDuration = lastHiddenAt > 0 ? now - lastHiddenAt : 0;

      if (debounceTimeout) clearTimeout(debounceTimeout);

      // Wait 2s so AuthContext's visibility handler can refresh the session first
      debounceTimeout = setTimeout(async () => {
        if (!user) return;

        lastRefreshTime = Date.now();
        setLastTabRefresh(lastRefreshTime);

        try {
          await refreshProfile();
        } catch {
          // Profile refresh can fail if session recovery is still in flight — ignore
        }

        // Broadcast refresh to all modules for any absence > 30 seconds
        if (absenceDuration > 30_000) {
          const moduleEvents = [
            'refresh-digital', 'refresh-legal', 'refresh-financial',
            'refresh-health', 'refresh-wellbeing', 'refresh-pets',
            'refresh-ndis', 'refresh-legacy', 'refresh-travel',
            'refresh-my-files', 'refresh-emergency', 'refresh-budgeting',
          ];
          moduleEvents.forEach(evt => window.dispatchEvent(new Event(evt)));
        }
      }, 2000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [user, refreshProfile]);

  // Removed auto-refresh score logic to prevent constant refreshing

  // Update local profile when profile changes, but don't wipe it on transient null
  useEffect(() => {
    if (profile) setLocalProfile(profile);
  }, [profile]);

  // State for sidebar items
  useEffect(() => {
    if (!user) return;
    
    const loadSidebarConfig = async () => {
      try {
        // First, try to load existing configuration
        const { data: existingConfig } = await supabase
          .from('user_preferences')
          .select('sidebar_items')
          .eq('user_id', user.id)
          .single();

        let currentItems = defaultSidebarItems;
        
        if (existingConfig?.sidebar_items) {
          try {
            const savedItems = JSON.parse(existingConfig.sidebar_items);
            
            // Check if budgeting module exists in saved config
            const hasBudgeting = savedItems.some((item: any) => item.id === 'budgeting');
            
            let needsUpdate = false;
            
            if (!hasBudgeting) {
              // Add budgeting module to existing config
              const budgetingItem = {
                id: 'budgeting',
                label: 'Budgeting',
                iconName: 'budgeting',
                section: 'budgeting',
                visible: true
              };
              
              // Insert budgeting after financial module
              const financialIndex = savedItems.findIndex((item: any) => item.id === 'financial');
              if (financialIndex !== -1) {
                savedItems.splice(financialIndex + 1, 0, budgetingItem);
              } else {
                savedItems.push(budgetingItem);
              }
              
              needsUpdate = true;
              console.log('Added budgeting module to existing user configuration');
            }
            
            // Add the emergency module for users whose saved config predates it.
            // If it's already present we leave its visibility alone — forcing it back
            // on here used to override the module choices made during onboarding.
            const emergencyIndex = savedItems.findIndex((item: any) => item.id === 'emergency');
            if (emergencyIndex === -1) {
              savedItems.push({
                id: 'emergency',
                label: 'Emergency',
                iconName: 'emergency',
                section: 'emergency',
                visible: true
              });
              needsUpdate = true;
            }

            // Disable calendar module if it exists in saved config
            const calendarIndex = savedItems.findIndex((item: any) => item.id === 'calendar');
            if (calendarIndex !== -1 && savedItems[calendarIndex].visible) {
              savedItems[calendarIndex].visible = false;
              needsUpdate = true;
            }
            
            if (needsUpdate) {
              currentItems = savedItems;
              
              // Update the database with the new configuration
              await supabase
                .from('user_preferences')
                .upsert({
                  user_id: user.id,
                  sidebar_items: JSON.stringify(currentItems),
                  updated_at: new Date().toISOString()
                }, { onConflict: 'user_id' });
            } else {
              currentItems = savedItems;
            }
          } catch (parseError) {
            console.error('Error parsing existing sidebar config:', parseError);
            // Use default items if parsing fails
            currentItems = defaultSidebarItems;
          }
        }
        
        setSidebarItems(currentItems);
        safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(currentItems));
        
      } catch (err) {
        console.error('Failed to load sidebar configuration:', err);
        // Use default items on error
        setSidebarItems(defaultSidebarItems);
        safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(defaultSidebarItems));
      }
    };
    
    loadSidebarConfig();
  }, [user]);

  const handleNavigation = (section: string) => {
    // Disabled modules - redirect to coming soon
    if (section === 'calendar') {
      setActiveSection('dashboard');
      navigate('/dashboard/coming-soon', { state: { module: section } });
      return;
    }
    setActiveSection(section);
    if (section === 'digital') {
      navigate('/dashboard/digital');
    } else if (section === 'legal') {
      navigate('/dashboard/legal');
    } else if (section === 'financial') {
      navigate('/dashboard/financial');
    } else if (section === 'budgeting') {
      navigate('/dashboard/budgeting');
    } else if (section === 'health') {
      navigate('/dashboard/health');
    } else if (section === 'wellbeing') {
      navigate('/dashboard/wellbeing');
    } else if (section === 'pets') {
      navigate('/dashboard/pets');
    } else if (section === 'ndis') {
      navigate('/dashboard/ndis');
    } else if (section === 'legacy') {
      navigate('/dashboard/legacy');
    } else if (section === 'travel') {
      navigate('/dashboard/travel');
    } else if (section === 'files') {
      navigate('/dashboard/files');
    } else if (section === 'emergency') {
      navigate('/dashboard/emergency');
    } else if (section === 'settings') {
      navigate('/dashboard/settings');
    } else {
      navigate('/dashboard');
    }
  };

  // Handle saving sidebar configuration
  const handleSaveSidebarConfig = async (items: SidebarItem[]) => {
    setSidebarItems(items);
    
    // Save to localStorage for immediate feedback
    if (user) {
      safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(items));
      
      // Save to database
      try {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            sidebar_items: JSON.stringify(items),
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
          
        if (error) {
          console.error('Failed to save sidebar configuration:', error);
        }
      } catch (err) {
        console.error('Error saving sidebar configuration:', err);
      }
    }
  };

  // Load widget preference from localStorage
  useEffect(() => {
    const savedPreference = safeLocalStorage.getItem('useCustomWidgets');
    if (savedPreference !== null) {
      setUseCustomWidgets(savedPreference === 'true');
    }
  }, []);

  // Get visible sidebar items with fallback to default items
  const visibleSidebarItems = useMemo(() => {
    const items = sidebarItems.length > 0 ? sidebarItems : defaultSidebarItems;
    return items.filter(item => item.visible);
  }, [sidebarItems, defaultSidebarItems]);

  // Custom function to get NDIS icon - using LifeBuoy instead of Accessibility
  const getNDISIcon = (size: number = 20) => {
    return <LifeBuoy size={size} />;
  };

  // Custom function to get Legacy icon
  const getLegacyIcon = (size: number = 20) => {
    return <BookOpen size={size} />;
  };
  
  // Custom function to get Travel icon
  const getTravelIcon = (size: number = 20) => {
    return <Plane size={size} />;
  };

  // Custom function to get Files icon
  const getFilesIcon = (size: number = 20) => {
    return <Folder size={size} />;
  };

  // Custom function to get Emergency icon
  const getEmergencyIcon = (size: number = 20) => {
    return <AlertTriangle size={size} />;
  };



  // Custom function to get Will icon
  const getWillIcon = (size: number = 20) => {
    return <FileText size={size} />;
  };

  // Extend the getIconComponent function to include NDIS, Legacy, Travel, Files, Family and Will
  const getExtendedIconComponent = (iconName: string, size: number = 20) => {
    if (iconName === 'ndis') {
      return getNDISIcon(size);
    }
    if (iconName === 'legacy') {
      return getLegacyIcon(size);
    }
    if (iconName === 'travel') {
      return getTravelIcon(size);
    }
    if (iconName === 'files') {
      return getFilesIcon(size);
    }

    if (iconName === 'will') {
      return getWillIcon(size);
    }
    if (iconName === 'emergency') {
      return getEmergencyIcon(size);
    }
    return getIconComponent(iconName, size);
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Main Sidebar */}
      <aside className="w-64 bg-idecide-primary flex flex-col">
        <div className="p-4">
          <div className="flex justify-center">
            <button 
              onClick={() => handleNavigation('dashboard')}
              className="bg-white p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              title="Go to Dashboard"
            >
              <img src="/idecide-logo.png" alt="iDecide logo" className="h-10 w-auto" />
            </button>
          </div>
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-3 pr-10 py-2 bg-white/80 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-secondary focus:border-transparent"
            />
          </div>
        </div>
        
        <nav className="flex-1">
          <div className="mb-8">
            <div className="px-4 py-2 text-sm font-medium text-idecide-dark">MY iDECIDE</div>
            
            {/* Render visible sidebar items */}
            {visibleSidebarItems.map(item => (
              <NavItem
                key={item.id}
                icon={getExtendedIconComponent(item.iconName, 20)}
                label={item.label}
                active={activeSection === item.section}
                onClick={() => handleNavigation(item.section)}
              />
            ))}
            {/* Add Settings NavItem */}
            <NavItem
              icon={<Settings size={20} />}
              label="Settings"
              active={activeSection === 'settings'}
              onClick={() => handleNavigation('settings')}
            />
          </div>
          
          {/* Action buttons moved higher up for better visibility */}
          <div className="border-t border-idecide-secondary/30 pt-4 mb-4">
            <div className="px-4 py-2 text-sm font-medium text-idecide-dark">ACTIONS</div>
            <NavItem
              icon={<Settings size={20} />}
              label="Customise Sidebar"
              onClick={() => setShowSidebarConfig(true)}
            />
            <NavItem
              icon={<Users size={20} />}
              label="Trusted Advisors"
              onClick={() => navigate('/dashboard/trusted-advisors')}
              active={window.location.pathname === '/dashboard/trusted-advisors'}
            />
            <NavItem
              icon={<Share2 size={20} />}
              label="Shared With Me"
              onClick={() => navigate('/dashboard/shared-with-me')}
              active={window.location.pathname === '/dashboard/shared-with-me'}
            />
            <NavItem
              icon={<LogOut size={20} />}
              label="Sign Out"
              onClick={signOut}
              className="text-red-600 hover:text-red-700"
            />
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-hidden">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <Breadcrumb />
        </div>
        
        <Routes>
          <Route path="digital/*" element={<DigitalEstatePlanning />} />
          <Route path="legal/*" element={<LegalEstatePlanning />} />
          <Route path="financial/*" element={<FinancialManagement />} />
          <Route path="budgeting/*" element={<BudgetingManagement />} />
          <Route path="health/*" element={<HealthManagement />} />

          <Route path="wellbeing/*" element={<WellbeingManagement />} />
          <Route path="pets/*" element={<PetsManagement />} />
          <Route path="ndis/*" element={<NDISManagement />} />
          <Route path="legacy/*" element={<LegacyManagement />} />
          <Route path="travel/*" element={<TravelManagement />} />
          <Route path="files/*" element={<MyFilesManagement />} />
          <Route path="emergency/*" element={<EmergencyManagement />} />
          <Route path="calendar/*" element={<ComingSoonPage module="Calendar" />} />
          <Route path="coming-soon" element={<ComingSoonPage />} />
          <Route path="settings" element={<SettingsDashboard />} />
          <Route path="trusted-advisors" element={<TrustedAdvisorManagement />} />
          <Route path="shared-with-me" element={<SharedWithMe />} />
          <Route path="/" element={
            <div className="max-w-4xl">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  Welcome back{localProfile?.full_name ? `, ${localProfile.full_name}` : ''}
                </h2>
                <p className="text-gray-600">
                  Your iDecide Score: <span className="font-semibold">{localProfile?.idecide_score || 0}</span>/500
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                {visibleSidebarItems
                  .filter(item => item.section !== 'dashboard')
                  .map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleNavigation(item.section)}
                      className="bg-idecide-primary p-6 rounded-lg transition-transform hover:scale-105 hover:shadow-lg text-left"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {getExtendedIconComponent(item.iconName, 24)}
                        </div>
                        <h3 className="text-lg font-semibold text-idecide-dark">{item.label}</h3>
                      </div>
                      <p className="text-gray-600">Click to manage</p>
                    </button>
                  ))}
              </div>
            </div>
          } />
        </Routes>
      </main>

      {/* Widget Sidebar */}
      <WidgetSidebar className="w-80" />

      {/* Sidebar Configuration Modal */}
      <SidebarConfigModal
        isOpen={showSidebarConfig}
        onClose={() => setShowSidebarConfig(false)}
        items={sidebarItems}
        onSave={handleSaveSidebarConfig}
      />
    </div>
  );
}